/**
 * Review-queue state machine for the agent HITL modal.
 *
 * Drives the one-topic-at-a-time review of a batch of PendingConfirmations,
 * expanded into ReviewUnits: most confirmations are one whole unit, but
 * multi-field single-record proposals (safety message, hazmat, incident info)
 * split into one unit per changed field. For each unit the reviewer can edit
 * fields inline, type a response, and pick a verdict (Accept / Reject). At
 * submit the unit states are folded back into ONE decision per confirmation
 * (precedence: respond text → reject → edit → approve; split confirmations
 * merge per-field verdicts, dropping rejected fields from the write — except a
 * rejected required-on-create field, which rejects the whole create), so a
 * single Submit still builds an ordered, length-N ResumeDecision[] for
 * /disasterchat/resume. Each new batch from the backend (a "cycle") re-seeds
 * the machine via reset(); approved/edited items are written and only responded
 * items return — so each cycle is narrower than the last.
 */
import { useReducer, useMemo, useCallback } from 'react';
import {
  buildEditedArgs,
  buildEditedArgsPartial,
  getFieldLabel,
  isRequiredOnCreate,
  isSplitConfirmation,
} from '../confirmationFieldConfig';
import type {
  PendingConfirmation,
  ResumeDecision,
  ReviewUnit,
  ItemState,
  ItemStatus,
  Phase,
} from './types';

export interface ReviewState {
  phase: Phase;
  cycle: number;
  /** Index into `units`. */
  index: number;
  items: PendingConfirmation[];
  /** The reviewable cards `items` expands into (see buildUnits). */
  units: ReviewUnit[];
  /** Per-unit review state for the current cycle, keyed by unit index. */
  decisions: Record<number, ItemState>;
}

export type ReviewAction =
  | { type: 'setVerdict'; verdict: 'accepted' | 'rejected' }
  | { type: 'setEdit'; field: string; value: unknown }
  | { type: 'setRespondText'; text: string }
  | { type: 'goto'; index: number }
  | { type: 'submit' }
  | { type: 'revertSubmit' }
  | { type: 'reset'; items: PendingConfirmation[]; cycle: number };

const entityOf = (item: PendingConfirmation): string | undefined =>
  typeof item.args?.entity === 'string'
    ? (item.args.entity as string)
    : undefined;

/**
 * Expand confirmations into review units: one per preview.diff field for split
 * confirmations, one whole unit otherwise. A split confirmation with no diff
 * still gets a whole unit — every confirmation must produce a decision.
 */
export function buildUnits(items: PendingConfirmation[]): ReviewUnit[] {
  const units: ReviewUnit[] = [];
  items.forEach((item, i) => {
    const diff = item.preview?.diff ?? [];
    if (
      isSplitConfirmation(item.tool_name, entityOf(item)) &&
      diff.length > 0
    ) {
      diff.forEach((d) => units.push({ confirmationIndex: i, field: d.field }));
    } else {
      units.push({ confirmationIndex: i });
    }
  });
  return units;
}

/**
 * Decisions available for one unit: the confirmation's allowed_decisions (empty
 * ⇒ all). Rejecting a required-on-create field is allowed and means "reject the
 * whole create" (see mergedDecision) — the remaining fields cannot form a valid
 * create without it.
 */
export function unitAllowedDecisions(
  item: PendingConfirmation,
  _unit: ReviewUnit
): ('approve' | 'edit' | 'reject')[] {
  const base =
    item.allowed_decisions && item.allowed_decisions.length > 0
      ? item.allowed_decisions
      : (['approve', 'edit', 'reject'] as const);
  return [...base];
}

export function init(items: PendingConfirmation[]): ReviewState {
  return {
    phase: 'review',
    cycle: 1,
    index: 0,
    items,
    units: buildUnits(items),
    decisions: {},
  };
}

const hasText = (s?: string): boolean => !!s && s.trim().length > 0;

/** A unit is decided once it has a verdict or a non-empty response. */
export function isDecided(state?: ItemState): boolean {
  return !!state && (!!state.verdict || hasText(state.respondText));
}

/** Resolved sidebar status for a unit's review state. */
export function itemStatus(state?: ItemState): ItemStatus {
  if (!state) return 'pending';
  if (hasText(state.respondText)) return 'edited';
  if (state.verdict === 'rejected') return 'rejected';
  if (state.verdict === 'accepted') {
    return state.edits && Object.keys(state.edits).length > 0
      ? 'edited'
      : 'approved';
  }
  return 'pending';
}

const valueToText = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '(empty)';
  if (Array.isArray(value)) return value.map(valueToText).join(', ');
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
};

/** Human-readable summary of a reviewer's inline edits, folded into a response. */
function serializeEdits(
  item: PendingConfirmation,
  edits?: Record<string, unknown>
): string {
  if (!edits || Object.keys(edits).length === 0) return '';
  const entity = entityOf(item);
  const parts = Object.entries(edits).map(
    ([field, value]) =>
      `${getFieldLabel(item.tool_name, entity, field)} → "${valueToText(value)}"`
  );
  return `\n\n[My inline edits: ${parts.join('; ')}]`;
}

/** Resolve one whole (unsplit) confirmation's decision — original precedence. */
function wholeDecision(
  item: PendingConfirmation,
  st: ItemState | undefined
): ResumeDecision {
  if (st && hasText(st.respondText)) {
    return {
      decision: 'reject',
      message: `${st.respondText!.trim()}${serializeEdits(item, st.edits)}`,
    };
  }
  if (st?.verdict === 'rejected') return { decision: 'reject' };
  if (
    st?.verdict === 'accepted' &&
    st.edits &&
    Object.keys(st.edits).length > 0
  ) {
    return {
      decision: 'edit',
      tool_name: item.tool_name,
      edited_args: buildEditedArgs(item.args, item.tool_name, st.edits),
    };
  }
  return { decision: 'approve' };
}

/** Fold a split confirmation's per-field unit states into one decision. */
function mergedDecision(
  item: PendingConfirmation,
  fieldStates: { field: string; st?: ItemState }[]
): ResumeDecision {
  const entity = entityOf(item);
  const label = (field: string): string =>
    getFieldLabel(item.tool_name, entity, field);

  const responded = fieldStates.filter(({ st }) => hasText(st?.respondText));
  const rejected = fieldStates.filter(
    ({ st }) => !hasText(st?.respondText) && st?.verdict === 'rejected'
  );
  // Undecided defaults to accept-as-proposed, matching the whole-unit default.
  const accepted = fieldStates.filter(
    ({ st }) => !hasText(st?.respondText) && st?.verdict !== 'rejected'
  );
  // Include responded units' edits too — the respond placeholder promises
  // "your edits above are included as context".
  const edits: Record<string, unknown> = {};
  for (const { st } of accepted) Object.assign(edits, st?.edits ?? {});
  for (const { st } of responded) Object.assign(edits, st?.edits ?? {});

  if (responded.length > 0) {
    const notes = responded
      .map(({ field, st }) => `${label(field)}: ${st!.respondText!.trim()}`)
      .join('\n');
    const rejectedNote =
      rejected.length > 0
        ? `\n\n[Rejected: ${rejected.map(({ field }) => label(field)).join(', ')}]`
        : '';
    const approvedPlain = accepted.filter(
      ({ st }) => !st?.edits || Object.keys(st.edits).length === 0
    );
    const approvedNote =
      approvedPlain.length > 0
        ? `\n\n[Approved as proposed: ${approvedPlain
            .map(({ field }) => label(field))
            .join(', ')}]`
        : '';
    return {
      decision: 'reject',
      message: `${notes}${serializeEdits(item, edits)}${rejectedNote}${approvedNote}`,
    };
  }
  // Rejecting a field the backend requires on create can't just drop that
  // field (the remaining create would fail validation on resume) — it rejects
  // the whole create.
  const rejectedRequired = rejected.filter(({ field }) =>
    isRequiredOnCreate(item.tool_name, entity, field)
  );
  if (rejectedRequired.length > 0) {
    return {
      decision: 'reject',
      message: `Rejected required field ${rejectedRequired
        .map(({ field }) => label(field))
        .join(', ')} — the proposed create was cancelled.`,
    };
  }
  if (accepted.length === 0) return { decision: 'reject' };
  if (rejected.length === 0 && Object.keys(edits).length === 0) {
    return { decision: 'approve' };
  }
  return {
    decision: 'edit',
    tool_name: item.tool_name,
    edited_args: buildEditedArgsPartial(
      item.args,
      item.tool_name,
      edits,
      rejected.map(({ field }) => field)
    ),
  };
}

/**
 * Build the ordered ResumeDecision[] for /disasterchat/resume — one decision
 * per confirmation (NOT per unit), in confirmation order. Pure (no React) so it
 * can be unit-tested directly.
 */
export function buildDecisionsFromState(
  items: PendingConfirmation[],
  units: ReviewUnit[],
  decisions: Record<number, ItemState>
): ResumeDecision[] {
  return items.map((item, i) => {
    const own = units
      .map((unit, unitIndex) => ({ unit, st: decisions[unitIndex] }))
      .filter(({ unit }) => unit.confirmationIndex === i);
    if (own.length === 1 && own[0].unit.field === undefined) {
      return wholeDecision(item, own[0].st);
    }
    return mergedDecision(
      item,
      own.map(({ unit, st }) => ({ field: unit.field!, st }))
    );
  });
}

/** Next unit index that isn't decided, scanning forward (wrapping); -1 if none. */
function nextPending(
  decisions: Record<number, ItemState>,
  from: number,
  total: number
): number {
  for (let step = 1; step <= total; step += 1) {
    const i = (from + step) % total;
    if (!isDecided(decisions[i])) return i;
  }
  return -1;
}

export function reducer(state: ReviewState, action: ReviewAction): ReviewState {
  switch (action.type) {
    case 'setVerdict': {
      const decisions: Record<number, ItemState> = {
        ...state.decisions,
        [state.index]: {
          ...state.decisions[state.index],
          verdict: action.verdict,
        },
      };
      const next = nextPending(decisions, state.index, state.units.length);
      return { ...state, decisions, index: next === -1 ? state.index : next };
    }
    case 'setEdit': {
      const current = state.decisions[state.index] ?? {};
      return {
        ...state,
        decisions: {
          ...state.decisions,
          [state.index]: {
            ...current,
            edits: { ...current.edits, [action.field]: action.value },
          },
        },
      };
    }
    case 'setRespondText':
      return {
        ...state,
        decisions: {
          ...state.decisions,
          [state.index]: {
            ...state.decisions[state.index],
            respondText: action.text,
          },
        },
      };
    case 'goto':
      return { ...state, index: action.index };
    case 'submit':
      return { ...state, phase: 'applying' };
    case 'revertSubmit':
      // Resume request failed — leave the spinner and let the reviewer retry.
      return { ...state, phase: 'review' };
    case 'reset':
      return {
        phase: 'review',
        cycle: action.cycle,
        index: 0,
        items: action.items,
        units: buildUnits(action.items),
        decisions: {},
      };
    default:
      return state;
  }
}

export function useReviewQueue(initialItems: PendingConfirmation[]) {
  const [state, dispatch] = useReducer(reducer, initialItems, init);

  const currentUnit = state.units[state.index];
  const currentItem = currentUnit
    ? state.items[currentUnit.confirmationIndex]
    : undefined;
  const currentState = state.decisions[state.index];

  const allDecided =
    state.units.length > 0 &&
    state.units.every((_, i) => isDecided(state.decisions[i]));

  const decidedCount = useMemo(
    () => state.units.filter((_, i) => isDecided(state.decisions[i])).length,
    [state.units, state.decisions]
  );

  const buildDecisions = useCallback(
    (): ResumeDecision[] =>
      buildDecisionsFromState(state.items, state.units, state.decisions),
    [state.items, state.units, state.decisions]
  );

  return {
    state,
    currentUnit,
    currentItem,
    currentState,
    allDecided,
    decidedCount,
    buildDecisions,
    setVerdict: (verdict: 'accepted' | 'rejected') =>
      dispatch({ type: 'setVerdict', verdict }),
    setEdit: (field: string, value: unknown) =>
      dispatch({ type: 'setEdit', field, value }),
    setRespondText: (text: string) =>
      dispatch({ type: 'setRespondText', text }),
    goto: (index: number) => dispatch({ type: 'goto', index }),
    submit: () => dispatch({ type: 'submit' }),
    revertSubmit: () => dispatch({ type: 'revertSubmit' }),
    reset: (items: PendingConfirmation[], cycle: number) =>
      dispatch({ type: 'reset', items, cycle }),
  };
}

export type ReviewQueue = ReturnType<typeof useReviewQueue>;
